import { FormattedMessage } from 'react-intl';
import { Box, FormLabel, Stack, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { DATE_FORMAT, SubSection } from '@ui-framework/index';
import dayjs from 'dayjs';
import ClaimStatus from '../../../ClaimStatus';
import UnPaginatedList from '../../../UnPaginatedList';

// create component which accepts user object  having first name and last name, and displays full name
function FullName({ user }) {
  return <span>{`${user.firstName} ${user.lastName}`}</span>;
}

function ClaimDetail({ claimData }) {
  const { createdDateTime, claimStatus, claimNumber } = claimData;
  // Only show one: reject reason takes precedence over approve override
  const renderReason = () => {
    if (claimStatus === 'REJECTED' || claimStatus === 'REJECTED_OVERRIDE') {
      return (
        <Grid2 container>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage
                id="CLAIM_REJECTED_DATE"
                defaultMessage="Claim Rejected Date"
              />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {claimData.rejectedDateTime
                  ? dayjs(claimData.rejectedDateTime).format(
                      DATE_FORMAT.LONG_DATE,
                    )
                  : '-'}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage id="REJECTED_BY" defaultMessage="Rejected By" />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                <FullName user={claimData.rejectedByUser} />
              </Typography>
            </Box>
          </Grid2>
          <Grid2 xs={12} md={6}>
            <FormLabel>
              <FormattedMessage
                id="REJECT_REASON"
                defaultMessage="Reject Reason"
              />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {claimData.rejectReasonCode
                  ? `${claimData.rejectReasonCode} - ${claimData.rejectReasonText}`
                  : '-'}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      );
    }
    if (claimStatus === 'APPROVED' || claimStatus === 'APPROVED_OVERRIDE') {
      return (
        <Grid2 container>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage
                id="CLAIM_APPROVED_DATE"
                defaultMessage="Claim Approved Date"
              />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {claimData.approvedDateTime
                  ? dayjs(claimData.approvedDateTime).format(
                      DATE_FORMAT.LONG_DATE,
                    )
                  : '-'}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage id="APPROVED_BY" defaultMessage="Approved By" />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                <FullName user={claimData.approvedByUser} />
              </Typography>
            </Box>
          </Grid2>

          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage
                id="APPROVED_OVERRIDE_REASON"
                defaultMessage="Approved Override Reason"
              />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {claimData.approveOverrideCode} -{' '}
                {claimData.approveOverrideText}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      );
    }
    return null;
  };

  return (
    <Stack spacing={1}>
      <SubSection>
        <Grid2 container>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage
                id="CLAIM_NUMBER"
                defaultMessage="Claim Number"
              />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {claimNumber}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage
                id="CLAIM_STATUS"
                defaultMessage="Claim Status"
              />
            </FormLabel>
            <Box>
              <ClaimStatus>{claimStatus}</ClaimStatus>
            </Box>
          </Grid2>
          <Grid2 xs={12} md={3}>
            <FormLabel>
              <FormattedMessage id="CREATED" defaultMessage="Created" />
            </FormLabel>
            <Box>
              <Typography
                color="tertiary.main"
                fontWeight="bold"
                variant="body2">
                {createdDateTime
                  ? dayjs(createdDateTime).format(DATE_FORMAT.LONG_DATE)
                  : createdDateTime}
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
        {renderReason()}
      </SubSection>

      {Array.isArray(claimData?.claimReviews) &&
        claimData?.claimReviews?.length > 0 && (
          <SubSection>
            <FormLabel error>
              <FormattedMessage
                id="REVIEW_REASON"
                defaultMessage="Review Reason"
              />
            </FormLabel>
            <UnPaginatedList
              items={claimData?.claimReviews?.map(r => ({
                label: r?.reviewReason,
              }))}
            />
          </SubSection>
        )}
    </Stack>
  );
}

export default ClaimDetail;
